import { adminApiClient } from './baseService'
import { buildModeAwareConfig } from './modeAware'

export interface UserInvitationEntry {
  userId: string; userEmail: string; userIsActive: boolean; code: string;
  status: 'active' | 'disabled'; createdAt: string; updatedAt: string; successfulInvites: number;
}
export interface UserReferralEntry { inviteeUserId: string; inviteeUserEmail: string; createdAt: string }
export interface InvitationPage<T> { entries: T[]; total: number; limit: number; offset: number }
export type QuotaSetting = { kind: 'inherit' } | { kind: 'unlimited' } | { kind: 'limited'; maxUses: number }
export interface QuotaTarget { scope: 'plan' | 'user'; targetId?: string }
export interface InvitationQuota {
  scope: QuotaTarget['scope']; targetId: string | null; setting: QuotaSetting;
  effectiveLimit: number | null; effectiveSource: string; effectivePlanId: string | null;
  usage: { successfulInvites: number; remaining: number | null } | null;
  planImpact: { memberUsers: number; individualOverrides: number } | null;
  affectedUserOverrides?: number;
}
export interface InvitationRequirements { minSettledAmountUsd: string; minSettledOrderCount: number }
export type PlanQuota = Exclude<QuotaSetting, { kind: 'inherit' }>
export type PlanSettings = { planId: string } & (
  { configurationState: 'unconfigured'; quota: null; eligibility: null; planImpact: null }
  | { configurationState: 'configured'; quota: PlanQuota; eligibility: InvitationRequirements;
      planImpact: { memberUsers: number; individualOverrides: number } })

const base = '/admin/system/merchant-user-invitations'
async function request<T>(path: string, params?: object, body?: unknown, create = false): Promise<T> {
  const config = { ...buildModeAwareConfig('mainnet'), params }
  const response = body === undefined ? await adminApiClient.get(path, config)
    : create ? await adminApiClient.post(path, body, config) : await adminApiClient.patch(path, body, config)
  if (!response.data || response.data.status !== 'success' || response.data.data === undefined) {
    throw new Error('Unable to load invitations')
  }
  return response.data.data as T
}
export const merchantUserInvitationService = {
  async resolveUserEmail(input: string): Promise<{ userId: string; email: string }> {
    const email = input.trim().toLowerCase()
    if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      throw new Error('INVITATION_EMAIL_INVALID')
    }
    // Preserve the raw endpoint contract: the generic user-list adapter normalizes missing lists to [].
    const response = await adminApiClient.get('/admin/platform-users', {
      ...buildModeAwareConfig('mainnet'), params: { email, page: 1, limit: 2 },
    })
    const payload = response.data
    const data = payload?.data
    const pagination = data?.pagination
    if (payload?.success !== true || !data || !Array.isArray(data.users)
      || !pagination || pagination.page !== 1 || pagination.limit !== 2
      || typeof pagination.total !== 'number' || !Number.isSafeInteger(pagination.total) || pagination.total < 0
      || pagination.totalPages !== Math.ceil(pagination.total / 2)
      || data.users.length !== Math.min(pagination.total, 2)
      || data.users.some((user: { id?: unknown; email?: unknown } | null) => !user
        || typeof user.id !== 'string' || !user.id.trim()
        || typeof user.email !== 'string' || user.email.toLowerCase() !== email)) {
      throw new Error('INVITATION_EMAIL_QUERY_FAILED')
    }
    if (pagination.total !== 1) {
      throw new Error('INVITATION_EMAIL_NOT_FOUND')
    }
    return { userId: data.users[0].id, email: data.users[0].email }
  },
  list: (params: { userId?: string; status?: string; limit: number; offset: number }) =>
    request<InvitationPage<UserInvitationEntry>>(base, params),
  referrals: (id: string, offset: number) => request<InvitationPage<UserReferralEntry>>(
    `${base}/${encodeURIComponent(id)}/referrals`, { limit: 100, offset }),
  status: (id: string, status: UserInvitationEntry['status']) =>
    request<UserInvitationEntry>(`${base}/${encodeURIComponent(id)}`, undefined, { status }),
  quota: (target: QuotaTarget) => request<InvitationQuota>('/admin/system/merchant-user-invitation-quotas', target),
  setQuota: (target: QuotaTarget, setting: QuotaSetting & { applyTo?: 'all' | 'inheriting' }) =>
    request<InvitationQuota>('/admin/system/merchant-user-invitation-quotas', target, setting),
  planSettings: (planId: string) => request<PlanSettings>(
    '/admin/system/merchant-user-invitation-plan-settings', { planId }),
  initializePlan: (planId: string, body: { quota: PlanQuota; eligibility: InvitationRequirements }) =>
    request<PlanSettings>('/admin/system/merchant-user-invitation-plan-settings', { planId }, body, true),
  requirements: (planId: string) => request<InvitationRequirements>(
    '/admin/system/merchant-user-invitation-eligibility', { planId }),
  setRequirements: (planId: string, body: InvitationRequirements) =>
    request<InvitationRequirements>('/admin/system/merchant-user-invitation-eligibility', { planId }, body),
}
